import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProfile, testAPI } from '../services/api';
import { SkeletonNavCard } from './SkeletonCard';
import './Home.css';

function isBirthdayToday(dob) {
  if (!dob) return false;
  const today = new Date();
  const birth = new Date(dob);
  return birth.getMonth() === today.getMonth() && birth.getDate() === today.getDate();
}

function Home() {
  const navigate = useNavigate();
  const username = localStorage.getItem('username');
  const [apiStatus, setApiStatus] = useState('checking');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!username) {
      navigate('/login');
      return;
    }
    checkAPI();
    fetchProfile();
  }, [username, navigate]);

  const checkAPI = async () => {
    try {
      await testAPI();
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('failed');
    }
  };

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const profileData = await getProfile(username);
      if (profileData.exists !== false && 
          profileData.maleName && 
          profileData.femaleName &&
          profileData.maleAge &&
          profileData.femaleAge) {
        setProfile(profileData);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const getWelcomeMessage = () => {
    if (loading) return "Welcome to Couple Check";
    if (!profile || !profile.maleName || !profile.femaleName || 
        !profile.maleAge || !profile.femaleAge) return "Welcome to Couple Check";
    return `Welcome ${profile.maleName} and ${profile.femaleName}!`;
  };

  const getCoupleDescription = () => {
    if (loading) return "Loading your profile...";
    if (!profile || !profile.maleName || !profile.femaleName || 
        !profile.maleAge || !profile.femaleAge) {
      return "Complete your profile to see a personalized description of your relationship journey.";
    }
    if (profile.aiGeneratedDescription) return profile.aiGeneratedDescription;
    if (profile.description) return profile.description;
    return "No description indicated. Visit your profile to add one or generate an AI description!";
  };

  const getBirthdayWishes = () => {
    if (!profile) return [];
    const wishes = [];
    if (isBirthdayToday(profile.maleDob)) wishes.push(profile.maleName);
    if (isBirthdayToday(profile.femaleDob)) wishes.push(profile.femaleName);
    return wishes;
  };

  const birthdayWishes = getBirthdayWishes();

  return (
    <div className="home-container">
      {/* Birthday Banner */}
      {!loading && birthdayWishes.length > 0 && (
        <div className="birthday-banner">
          <span className="birthday-confetti">🎂</span>
          <div className="birthday-text">
            <strong>Happy Birthday, {birthdayWishes.join(' & ')}! 🎉</strong>
            <span>Wishing you a wonderful day filled with love and joy!</span>
          </div>
          <span className="birthday-confetti">🎈</span>
        </div>
      )}

      {/* Hero Section */}
      <section className="hero">
        <h1 className="hero-title">{getWelcomeMessage()}</h1>
        <p className="hero-subtitle">
          Your personal space to nurture and grow your relationship together
        </p>
        
        <div className="api-status">
          {apiStatus === 'checking' && (
            <span className="status-checking">🔄 Connecting to server...</span>
          )}
          {apiStatus === 'failed' && (
            <span className="status-failed">❌ Backend connection failed</span>
          )}
        </div>
      </section>

      {/* Couple Description Section */}
      <section className="couple-description-section">
        <div className="description-card">
          <div className="description-icon">💑</div>
          <h2>About Your Relationship</h2>
          {loading ? (
            <div className="skeleton-description">
              <div className="skeleton-line skeleton-line--full" />
              <div className="skeleton-line skeleton-line--lg" />
              <div className="skeleton-line skeleton-line--md" />
            </div>
          ) : (
            <>
              <p className="couple-description">{getCoupleDescription()}</p>
              {(!profile || !profile.maleName || !profile.femaleName) && (
                <button
                  className="complete-profile-btn"
                  onClick={() => navigate('/profile')}
                >
                  Complete Your Profile
                </button>
              )}
            </>
          )}
        </div>
      </section>

      {/* Navigation Cards */}
      <section className="navigation-section">
        <div className="nav-cards">
          {loading ? (
            <>
              <SkeletonNavCard />
              <SkeletonNavCard />
              <SkeletonNavCard />
              <SkeletonNavCard />
            </>
          ) : (
            <>
              <div className="nav-card" onClick={() => navigate('/add-entry')}>
                <div className="card-icon">📝</div>
                <h3>Add Entry</h3>
                <p>Record your daily relationship experiences and feelings</p>
                <button className="card-button">Get Started →</button>
              </div>

              <div className="nav-card" onClick={() => navigate('/view-entries')}>
                <div className="card-icon">📖</div>
                <h3>View Entries</h3>
                <p>Browse through your journaling history and memories</p>
                <button className="card-button">View All →</button>
              </div>

              <div className="nav-card" onClick={() => navigate('/reports')}>
                <div className="card-icon">📊</div>
                <h3>Reports</h3>
                <p>Generate insights and track your relationship progress</p>
                <button className="card-button">View Reports →</button>
              </div>

              <div className="nav-card subscribe-card" onClick={() => navigate('/plus')}>
                <div className="card-icon">⭐</div>
                <h3>Couple Check Plus</h3>
                <p>Unlock unlimited entries, AI feedback, private entries and more</p>
                <button className="card-button subscribe-btn">Subscribe →</button>
              </div>
            </>
          )}
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works">
        <h2>How It Works</h2>
        <div className="steps">
          <div className="step">
            <div className="step-number">1</div>
            <h3>Complete Your Profile</h3>
            <p>Set up your couple profile with your names and story</p>
          </div>
          <div className="step">
            <div className="step-number">2</div>
            <h3>Journal Together</h3>
            <p>Both partners record daily experiences across 10 relationship categories</p>
          </div>
          <div className="step">
            <div className="step-number">3</div>
            <h3>Track & Grow</h3>
            <p>View insights, identify patterns, and strengthen your bond</p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;